import { MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import type { CashFlowSettingsConfiguration, CurrencyDimensionMeta } from "../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch.ts";
import { selectTabularSettingsState } from "../../../store/selectors/fieldsStateSelectors.ts";
import { fieldsStateActions } from "../../../store/thunks/fieldStatesThunks.ts";
import SwitchField from "../../common/SwitchField.tsx";
import FormatSettings from "../common/fields/format/FormatSettings.tsx";
import { getCashFlowSettings } from "./getCashFlowSettings.ts";

const sectionOptions: { value: CashFlowSettingsConfiguration["sections"]; caption: string }[] = [
  { value: "Combined", caption: "Off" },
  { value: "ContributionsAndDistributions", caption: "Contributions and distributions" },
  { value: "InvestmentsAndProceeds", caption: "Investments and proceeds" },
];

// Amounts carry no row currency field, so only a fixed currency can be picked.
const noCurrencyDimensions: CurrencyDimensionMeta[] = [];

interface Props {
  format?: boolean;
  readonly: boolean;
}

const CashFlowSettings = ({ format, readonly }: Props) => {
  const dispatch = useAppDispatch();
  const settings = useSelector(selectTabularSettingsState);
  const value = getCashFlowSettings(settings);
  const update = (changes: Partial<CashFlowSettingsConfiguration>) =>
    dispatch(fieldsStateActions.updateSettings({ settings: { cashFlow: { ...value, ...changes } } }));
  return (
    <Stack spacing={1.5}>
      {format ? (
        <FormatSettings
          Label={
            <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
              Amounts
            </Typography>
          }
          format={value.amountFormat}
          currencyDimensions={noCurrencyDimensions}
          onUpdateFormatter={(amountFormat) => update({ amountFormat })}
          readonlyMode={readonly}
        />
      ) : (
        <>
          <TextField
            select
            label="Default view"
            value={value.displayMode}
            disabled={readonly}
            onChange={(event) => {
              const mode = event.target.value;
              if (mode === "Summary" || mode === "Details" || mode === "Both") {
                update({ displayMode: mode });
              }
            }}
          >
            {["Summary", "Details", "Both"].map((mode) => (
              <MenuItem key={mode} value={mode}>
                {mode}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Split cash flows"
            value={value.sections}
            disabled={readonly}
            onChange={(event) => {
              const option = sectionOptions.find((item) => item.value === event.target.value);
              if (option) {
                update({ sections: option.value });
              }
            }}
          >
            {sectionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.caption}
              </MenuItem>
            ))}
          </TextField>
          <SwitchField
            label="Show total column"
            tooltipText="When Split by has selected values: add a Total column with the cash flows and IRR of all selected values together."
            checked={value.showSplitTotal}
            onChange={(showSplitTotal) => update({ showSplitTotal })}
            disabled={readonly}
          />
          <SwitchField
            label="Show zero totals"
            tooltipText="Summary and Both views: show dates whose total is 0. In Both view, dates with offsetting transactions stay visible."
            checked={value.showZeroTotals}
            onChange={(showZeroTotals) => update({ showZeroTotals })}
            disabled={readonly}
          />
          <SwitchField
            label="Expand transactions by default"
            tooltipText="Both view: show each date's transactions under its total when the report opens, and expand them in the Excel export."
            checked={value.expandTransactions}
            onChange={(expandTransactions) => update({ expandTransactions })}
            disabled={readonly}
          />
        </>
      )}
    </Stack>
  );
};

export default CashFlowSettings;
